/**
 * Input Recipe - Modern Chakra UI v3 Implementation
 *
 * Design Principles:
 * - Consistent border styling
 * - Clear focus states
 * - Accessible error states
 * - Proper text styles
 */

import { defineRecipe } from '@chakra-ui/react';

export const inputRecipe = defineRecipe({
  className: 'chakra-input',

  base: {
    width: '100%',
    borderStyle: 'solid',
    borderWidth: '1px',
    borderRadius: 'l',
    borderColor: 'border.accent',
    color: 'text.primary',
    bg: 'background.secondary',

    _placeholder: {
      color: 'text.quaternary',
    },
    _hover: {
      borderColor: 'border.accent.hover',
    },
    _focus: {
      borderColor: 'border.accent.active',
      outline: 'none',
      _hover: {
        borderColor: 'border.accent.active',
      },
    },
    _focusVisible: {
      focusRing: 'none',
    },
    _invalid: {
      borderColor: 'border.error',
      _hover: {
        borderColor: 'border.error',
      },
    },
    _disabled: {
      cursor: 'not-allowed',
      opacity: 'disabled',
    },
  },

  variants: {
    /** Bordered field (default). */
    variant: {
      primary: {
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'border.accent',
        _focus: {
          borderColor: 'border.accent.active',
          boxShadow: 'none',
        },
        _invalid: {
          borderColor: 'text.error',
          _focus: {
            borderColor: 'text.error',
            boxShadow: 'none',
          },
        },
      },
      outline: {
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'border.accent',
        _focus: {
          borderColor: 'border.accent.active',
          boxShadow: 'none',
        },
        _invalid: {
          borderColor: 'text.error',
          _focus: {
            borderColor: 'text.error',
            boxShadow: 'none',
          },
        },
        _disabled: {
          bg: 'background.canvas',
          color: 'text.tertiary',
          cursor: 'not-allowed',
          opacity: 'disabled',
        },
      },
      /** Borderless shell; use for inline titles — callers often set `bg` / `backgroundColor`. */
      subtle: {
        border: 'none',

        _focus: {
          focusRing: 'none',
          outline: 'none',
          outlineStyle: 'none',
        },
        _focusVisible: {
          focusRing: 'none',
          outline: 'none',
          outlineStyle: 'none',
        },
        _focusWithin: {
          focusRing: 'none',
          outline: 'none',
          outlineStyle: 'none',
        },
      },
    },
    size: {
      md: {
        textStyle: 'h6.regular',
        py: 's',
        px: 's',
        // reset variable and set fixed paddings left & right.
        // By default there are calculations for paddingInlines based on InputHeight, which we disable with this
        '--input-height': 'initial',
        paddingInlineStart: '36px !important',
        paddingInlineEnd: '36px !important',
      },
      /**
       * TODO: align with design
       * Same rhythm as `md` (`textStyle`, `py` / `px`) but **no** icon-slot insets (`36px` gutters).
       * Use with `InputGroup` when start/end elements are absent, or when layout handles inset outside.
       */
      mdNoInset: {
        textStyle: 'h6.regular',
        py: 's',
        px: 's',
      },
      /**
       * TODO: align with design
       * Embedded field inside custom layouts (e.g. chip field): no recipe icon gutters.
       * Parent provides inset; no `!important` so callers can tune via style props if needed.
       */
      chip: {
        textStyle: 'body.m.regular',
        py: 0,
        px: 0,
        '--input-height': 'initial',
        paddingInlineStart: 0,
        paddingInlineEnd: 0,
      },
    },
  },

  defaultVariants: {
    variant: 'primary',
    size: 'md',
  },
});
